import { useState } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import { printContract } from "@/lib/printContract";
import PaymentStatusBadge from "@/components/base/PaymentStatusBadge";
import StatusBadge from "@/components/base/StatusBadge";

const BRAND = "#C8A96E";
const BRAND_DARK = "#9a7840";

/* ─── Types ─────────────────────────────────────────── */
export type PortalContract = {
  id: string;
  contract_number: string;
  title: string;
  status: string;
  event_date: string;
  event_location: string;
  package_name: string;
  total_amount: number;
  deposit_amount: number;
  balance_amount: number;
  rendered_content: string | null;
  signature_status: string;
  client_signature: string | null;
  signed_at: string | null;
  payment_link: string | null;
  payment_status: string;
};

interface Props {
  contract: PortalContract;
  portalToken: string;
  clientId: string;
  clientName?: string;
  tenantName?: string;
  onSigned?: (contractId: string) => void;
}

/* ─── Helper: detect HTML content ───────────────────── */
function isHtmlContent(content: string): boolean {
  return /<[a-z][\s\S]*>/i.test(content);
}

/* ─── Component ─────────────────────────────────────── */
export default function ContractCard({
  contract: initialContract,
  portalToken,
  clientId,
  clientName = "",
  tenantName = "Photography Studio",
  onSigned,
}: Props) {
  const [contract, setContract] = useState<PortalContract>(initialContract);
  const [expanded, setExpanded] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [sigText, setSigText] = useState("");
  const [signing, setSigning] = useState(false);
  const [showSigPanel, setShowSigPanel] = useState(false);

  const paid = Number(contract.total_amount) - Number(contract.balance_amount);
  const pct =
    contract.total_amount > 0
      ? Math.min(100, Math.round((paid / Number(contract.total_amount)) * 100))
      : 0;
  const isSigned = contract.signature_status === "signed";
  const hasContent = !!contract.rendered_content?.trim();
  const isHtml = hasContent && isHtmlContent(contract.rendered_content!);

  /* ── Sign handler ── */
  const handleSign = async () => {
    if (!agreed || !sigText.trim()) return;

    setSigning(true);
    try {
      const { data, error } = await supabase.functions.invoke("sign-contract", {
        body: {
          contract_id: contract.id,
          client_id: clientId,
          token: portalToken,
          signature_text: sigText.trim(),
        },
      });

      if (error || !data?.success) {
        await Swal.fire({
          icon: "error",
          title: "Signing Failed",
          text: data?.error ?? "Could not save your signature. Please try again.",
          background: "#fff",
          color: "#0f172a",
          confirmButtonColor: BRAND,
        });
        setSigning(false);
        return;
      }

      /* Update local state */
      setContract((prev) => ({
        ...prev,
        signature_status: "signed",
        client_signature: sigText.trim(),
        signed_at: data.contract?.signed_at ?? new Date().toISOString(),
        status: "signed",
      }));

      onSigned?.(contract.id);
      setShowSigPanel(false);

      await Swal.fire({
        icon: "success",
        title: "Contract Signed!",
        html: `<p style="color:#64748b;font-size:14px;line-height:1.6">Your signature <strong style="color:${BRAND}">&ldquo;${sigText.trim()}&rdquo;</strong> has been recorded.<br>A copy of this agreement is stored securely.</p>`,
        background: "#fff",
        color: "#0f172a",
        confirmButtonColor: BRAND,
        confirmButtonText: "Done",
      });
    } catch {
      await Swal.fire({
        icon: "error",
        title: "Connection Error",
        text: "Unable to reach the server. Please check your internet connection.",
        background: "#fff",
        color: "#0f172a",
        confirmButtonColor: BRAND,
      });
    }
    setSigning(false);
  };

  /* ── Confirm intent ── */
  const handleSignIntent = async () => {
    if (!agreed || !sigText.trim()) return;

    const result = await Swal.fire({
      icon: "question",
      title: "Sign this contract?",
      html: `<p style="color:#64748b;font-size:14px;line-height:1.6">By signing as <strong style="color:${BRAND}">&ldquo;${sigText.trim()}&rdquo;</strong> you confirm that you have read and agreed to all terms.<br>This action cannot be undone.</p>`,
      background: "#fff",
      color: "#0f172a",
      showCancelButton: true,
      confirmButtonColor: BRAND,
      cancelButtonColor: "#94a3b8",
      confirmButtonText: "Yes, Sign Contract",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) handleSign();
  };

  /* ── Render ── */
  return (
    <div
      className={`bg-white rounded-xl border overflow-hidden transition-all ${
        isSigned ? "border-emerald-200" : "border-slate-200"
      }`}
    >
      {/* ─ Signed banner ─ */}
      {isSigned && (
        <div className="flex items-center gap-2 px-5 py-2.5 bg-emerald-50 border-b border-emerald-100">
          <span className="w-4 h-4 flex items-center justify-center text-emerald-500">
            <i className="ri-shield-check-fill text-sm"></i>
          </span>
          <span className="text-emerald-700 text-xs font-semibold">
            Electronically signed
            {contract.signed_at && (
              <span className="font-normal text-emerald-600 ml-1">
                — {new Date(contract.signed_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
              </span>
            )}
          </span>
        </div>
      )}

      {/* ─ Pending signature banner ─ */}
      {!isSigned && hasContent && (
        <div className="flex items-center gap-2 px-5 py-2.5 bg-amber-50 border-b border-amber-100">
          <span className="w-4 h-4 flex items-center justify-center text-amber-500">
            <i className="ri-pen-nib-line text-sm"></i>
          </span>
          <span className="text-amber-700 text-xs font-semibold">Awaiting your signature</span>
        </div>
      )}

      {/* ─ Main content ─ */}
      <div className="p-5">
        {/* Header row */}
        <div className="flex items-start justify-between mb-4">
          <div className="min-w-0">
            <p className="text-[#0f172a] font-semibold text-sm leading-tight">{contract.title}</p>
            <p className="text-slate-400 text-xs mt-0.5 font-mono">{contract.contract_number}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-3 flex-wrap justify-end">
            <PaymentStatusBadge status={contract.payment_status} />
            <StatusBadge status={contract.status} />
          </div>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <p className="text-slate-400 text-xs mb-0.5">Event Date</p>
            <p className="text-slate-700 text-sm font-medium">{contract.event_date ?? "—"}</p>
          </div>
          <div>
            <p className="text-slate-400 text-xs mb-0.5">Package</p>
            <p className="text-slate-700 text-sm font-medium truncate">{contract.package_name ?? "—"}</p>
          </div>
          <div>
            <p className="text-slate-400 text-xs mb-0.5">Total</p>
            <p className="text-[#0f172a] font-bold text-sm">${Number(contract.total_amount).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-slate-400 text-xs mb-0.5">Balance</p>
            <p className={`font-bold text-sm ${Number(contract.balance_amount) > 0 ? "text-amber-500" : "text-emerald-500"}`}>
              ${Number(contract.balance_amount).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Location */}
        {contract.event_location && (
          <div className="mt-3 flex items-center gap-1.5 text-slate-400 text-xs">
            <span className="w-3 h-3 inline-flex items-center justify-center">
              <i className="ri-map-pin-line text-[10px]"></i>
            </span>
            {contract.event_location}
          </div>
        )}

        {/* Payment progress */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-slate-400 mb-1.5">
            <span>Payment progress</span>
            <span className="font-medium" style={{ color: pct >= 100 ? "#10b981" : BRAND_DARK }}>
              {pct}%
            </span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-1.5">
            <div
              className="h-1.5 rounded-full transition-all"
              style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? "#10b981" : BRAND }}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {hasContent && (
            <button
              onClick={() => setExpanded((p) => !p)}
              className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-[#0f172a] bg-slate-50 hover:bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className={expanded ? "ri-eye-off-line" : "ri-eye-line"}></i>
              {expanded ? "Hide Contract" : "View Contract"}
            </button>
          )}

          <button
            onClick={() =>
              printContract(
                {
                  contract_number: contract.contract_number,
                  title: contract.title,
                  status: contract.status,
                  event_date: contract.event_date,
                  event_location: contract.event_location,
                  package_name: contract.package_name,
                  total_amount: contract.total_amount,
                  deposit_amount: contract.deposit_amount,
                  balance_amount: contract.balance_amount,
                  rendered_content: contract.rendered_content,
                  signature_status: contract.signature_status,
                  client_signature: contract.client_signature,
                  signed_at: contract.signed_at,
                  client_name: clientName,
                },
                tenantName,
              )
            }
            className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-[#0f172a] bg-slate-50 hover:bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-printer-line"></i>
            Print / PDF
          </button>

          {!isSigned && hasContent && (
            <button
              onClick={() => {
                setShowSigPanel(true);
                setExpanded(true);
              }}
              className="flex items-center gap-1.5 text-xs font-semibold text-white px-4 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap hover:opacity-90"
              style={{ backgroundColor: BRAND }}
            >
              <i className="ri-pen-nib-line"></i>
              Sign Contract
            </button>
          )}

          {!hasContent && (
            <span className="text-slate-300 text-xs italic flex items-center gap-1">
              <i className="ri-time-line text-[10px]"></i>
              Contract content coming soon
            </span>
          )}
        </div>

        {/* Pay Securely */}
        {contract.payment_link && contract.payment_status !== "paid" && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between gap-3">
              <p className="text-slate-400 text-xs">
                {contract.payment_status === "partial" || contract.payment_status === "deposit_paid"
                  ? "Balance pending — pay securely online"
                  : "Payment pending for this contract"}
              </p>
              <a
                href={contract.payment_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-white text-xs font-bold px-4 py-2 rounded-lg transition-all cursor-pointer whitespace-nowrap flex-shrink-0 hover:opacity-90"
                style={{ backgroundColor: BRAND }}
              >
                <i className="ri-secure-payment-line"></i>
                Pay Securely
                <i className="ri-external-link-line text-[10px]"></i>
              </a>
            </div>
          </div>
        )}

        {/* Fully paid confirmation */}
        {contract.payment_status === "paid" && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 text-emerald-600">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-checkbox-circle-fill text-sm"></i>
              </span>
              <p className="text-xs font-medium">Payment complete — thank you!</p>
            </div>
          </div>
        )}
      </div>

      {/* ─ Expanded: contract document + signature panel ─ */}
      {expanded && hasContent && (
        <div className="border-t border-slate-100">
          {/* Contract document */}
          <div className="px-5 py-4">
            <div className="bg-[#f8f9fb] rounded-xl border border-slate-200 p-5 md:p-6 max-h-[520px] overflow-y-auto">
              {/* Document header */}
              <div className="text-center mb-5 pb-4 border-b border-slate-200">
                <p className="text-slate-400 text-[10px] uppercase tracking-widest mb-1">Photography Contract</p>
                <h3 className="text-[#0f172a] font-bold text-base">{contract.title}</h3>
                <p className="text-slate-400 text-xs mt-1 font-mono">{contract.contract_number}</p>
              </div>

              {/* Content: render HTML if detected, otherwise plain text */}
              {isHtml ? (
                <div
                  className="prose prose-sm max-w-none text-slate-700 prose-headings:text-[#0f172a] prose-headings:font-semibold prose-p:leading-relaxed prose-strong:text-[#0f172a]"
                  dangerouslySetInnerHTML={{ __html: contract.rendered_content! }}
                />
              ) : (
                <pre className="text-slate-600 text-xs leading-relaxed whitespace-pre-wrap font-sans">
                  {contract.rendered_content}
                </pre>
              )}
            </div>
          </div>

          {/* ─ Signature Panel ─ */}
          {showSigPanel && !isSigned && (
            <div className="px-5 pb-5">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {/* Panel header */}
                <div
                  className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100"
                  style={{ backgroundColor: `${BRAND}10` }}
                >
                  <div
                    className="w-7 h-7 flex items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${BRAND}20` }}
                  >
                    <i className="ri-pen-nib-line text-sm" style={{ color: BRAND_DARK }}></i>
                  </div>
                  <div>
                    <p className="text-[#0f172a] font-semibold text-sm">Electronic Signature</p>
                    <p className="text-slate-400 text-xs">Legally binding — same effect as handwritten</p>
                  </div>
                </div>

                <div className="p-5 space-y-4">
                  {/* Agreement checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="relative flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                          agreed
                            ? "border-[#C8A96E] bg-[#C8A96E]"
                            : "border-slate-300 bg-transparent group-hover:border-slate-400"
                        }`}
                      >
                        {agreed && <i className="ri-check-line text-white text-xs font-bold"></i>}
                      </div>
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      I have carefully read and understood all terms and conditions in this contract, and I agree to be
                      bound by them in full.
                    </p>
                  </label>

                  {/* Signature input */}
                  <div>
                    <label className="block text-slate-500 text-xs font-semibold mb-2 uppercase tracking-wider">
                      Type your full legal name as your signature
                    </label>
                    <input
                      type="text"
                      value={sigText}
                      onChange={(e) => setSigText(e.target.value)}
                      placeholder="e.g. Jane Smith"
                      disabled={!agreed}
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-[#0f172a] placeholder-slate-300 outline-none transition-all text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{
                        fontFamily: sigText && agreed ? "'Georgia', serif" : "inherit",
                        fontSize: sigText && agreed ? "16px" : "14px",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && agreed && sigText.trim()) handleSignIntent();
                      }}
                    />

                    {/* Signature preview */}
                    {sigText && agreed && (
                      <div className="mt-3 px-5 py-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
                        <p className="text-slate-400 text-[10px] uppercase tracking-widest mb-1.5">Signature Preview</p>
                        <p
                          className="text-lg"
                          style={{ color: BRAND_DARK, fontFamily: "'Georgia', serif" }}
                        >
                          {sigText}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Legal disclaimer */}
                  <div className="bg-slate-50 rounded-lg px-4 py-3 border border-slate-100">
                    <p className="text-slate-400 text-[11px] leading-relaxed">
                      <i className="ri-information-line mr-1"></i>
                      By clicking &ldquo;Sign Contract&rdquo;, your typed name constitutes your electronic signature
                      with the same legal effect as a handwritten signature. Date:{" "}
                      {new Date().toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                      .
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowSigPanel(false);
                        setAgreed(false);
                        setSigText("");
                      }}
                      className="flex-1 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-500 text-sm font-medium py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSignIntent}
                      disabled={!agreed || !sigText.trim() || signing}
                      className="flex-1 flex items-center justify-center gap-2 text-white text-sm font-bold py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
                      style={{ backgroundColor: BRAND }}
                    >
                      {signing ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <>
                          <i className="ri-pen-nib-line"></i> Sign Contract
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─ Already signed state ─ */}
          {isSigned && (
            <div className="px-5 pb-5">
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                  <i className="ri-shield-check-line text-emerald-600"></i>
                </div>
                <div>
                  <p className="text-emerald-700 font-semibold text-sm">Contract Signed</p>
                  {contract.client_signature && (
                    <p className="text-slate-500 text-xs mt-1">
                      Signature:{" "}
                      <span style={{ fontFamily: "'Georgia', serif", color: BRAND_DARK, fontSize: "15px" }}>
                        {contract.client_signature}
                      </span>
                    </p>
                  )}
                  {contract.signed_at && (
                    <p className="text-slate-400 text-xs mt-0.5">
                      {new Date(contract.signed_at).toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  )}
                  <p className="text-slate-400 text-[11px] mt-1.5 flex items-center gap-1">
                    <i className="ri-lock-line"></i>
                    Stored securely · Cannot be modified
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}